import { Key, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Table } from '../../../../components/Table'
import { PaymentActions } from '../../PaymentActions/PaymentActions'
import { getUser } from '../../../../redux/store/user/getters'
import { useFinancialsAdminContext } from '../../Providers/FinancialsAdminProvider'
import { columns } from './constants'
import { useFinancialsContext } from '../../Providers/FinancialsProvider'
import { useFinancialsAbility } from '../../Providers/FinancialsAbilityProvider'
import { getDisabledStatuses } from '../../utils'
import { ACTIONS, SUBJECTS, contractSubject } from '../../Abilities'
import { LED_BY_VALUE } from '../../../../components/LeadByIndicator/constants'
import { adminTabs } from '../constants'
import { DEFAULT_PAGE } from '../../../../components/Table/constants'
import { PAYMENT_TYPES, SelectedRowData } from '../../types'
import { CognusAdminData } from './types'

export const AdminTable = () => {
  const [selectedRowData, setSelectedRowData] =
    useState<SelectedRowData | null>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])
  const {
    state: {
      totalNumber,
      tableData,
      pageInfo,
      sortParams,
      firstLoad,
      activeTab,
      period,
      filters
    },
    actions: { getTableData, updateCognusAdminData, applyFilters, setPageInfo }
  } = useFinancialsAdminContext()

  const {
    actions: { generateAndDownloadAdminDocument }
  } = useFinancialsContext()

  const user = useSelector(getUser)
  const ability = useFinancialsAbility()

  const hideHSName = useMemo(
    () =>
      ability.cannot(
        ACTIONS.VIEW,
        contractSubject(SUBJECTS.ADMIN_HS_NAME, user)
      ),
    [ability, user]
  )
  const isReceivableTab = activeTab === adminTabs.receivable
  const canSeeSubTabs = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.ADMIN_SUBTABS),
    [ability]
  )
  const canSeeInvoices = useMemo(
    () => !!user.vendor || (!isReceivableTab && canSeeSubTabs),
    [canSeeSubTabs, isReceivableTab, user.vendor]
  )

  useEffect(() => {
    if (period && activeTab) {
      void getTableData({ ...sortParams, filters, page: DEFAULT_PAGE })
      setSelectedRowKeys([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, activeTab])

  const handleTableChange = (pagination, _, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    void getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    void getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }

  const onResetSelection = () => {
    setSelectedRowData(null)
    setSelectedRowKeys([])
  }

  const updateTableData = async () => {
    try {
      await updateCognusAdminData()
      onResetSelection()
    } finally {
    }
  }

  const handleSelectRow = (selectedRowKeys, selectedRowData) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRowData(selectedRowData)
  }
  return (
    <div>
      <PaymentActions
        type={PAYMENT_TYPES.ADMIN_FEE}
        totalNumber={totalNumber}
        pageInfo={pageInfo}
        selectedRowData={selectedRowData}
        selectedLength={selectedRowKeys.length}
        updateTableData={updateTableData}
        onResetSelection={onResetSelection}
      />
      {firstLoad ? null : (
        <Table<CognusAdminData>
          dataSource={tableData}
          columns={columns({
            hideHSName,
            isReceivableTab,
            canSeeInvoices,
            filters,
            applyFilters,
            generateDocument: generateAndDownloadAdminDocument,
            userRole: user.role
          })}
          className="financials-table"
          onChange={handleTableChange}
          onChangePage={handleChangePageSize}
          pageSize={pageInfo.pageSize}
          pagination={{
            pageSize: pageInfo.pageSize,
            current: pageInfo.pageNumber,
            total: totalNumber
          }}
          rowSelection={
            user?.vendor
              ? {
                  selectedRowKeys,
                  onChange: handleSelectRow,
                  getCheckboxProps: (record) => ({
                    disabled:
                      getDisabledStatuses(
                        user.role,
                        record.due_date_status
                      ).includes(record.status) ||
                      !record.led_by ||
                      ![
                        LED_BY_VALUE.COMMUNITY,
                        LED_BY_VALUE.NATIONAL_GPO
                      ].includes(record?.led_by)
                  })
                }
              : undefined
          }
        />
      )}
    </div>
  )
}
